import Phaser from 'phaser';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { SfxManager } from '../utils/SfxManager';
import {
  initGame, placeBid, callLiar, callExact, continueAfterReveal, getWinnerId, aiDecide,
  breakdownMatching, GameState, DiceFace, Bid, AiProfile,
} from '../systems/LiarsDice';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE TAVERNE — Liar's Dice ===
// Sprint 80 (Phase 7) : mini-jeu Taverne contre 1-3 PNJ pirates.
// Niveau Taverne : 1=1 PNJ, 2=2 PNJ, 3=3 PNJ.
// Mises : 5 / 10 / 25 or selon niveau.

const HEADER_H = 70;

// Sprint 80 v2 — chaque PNJ a un profil d'IA distinct pour varier la difficulté
const NPCS: Array<{ name: string; profile: AiProfile; tag: string }> = [
  { name: 'Boucan',         profile: 'aggressive', tag: '🔥 Bluffeur'    },
  { name: 'Vieille Marie',  profile: 'cautious',   tag: '🦉 Méfiante'    },
  { name: 'Crochet Tordu',  profile: 'balanced',   tag: '⚖️ Équilibré'   },
  { name: 'Sale-Gosse',     profile: 'aggressive', tag: '🔥 Bluffeur'    },
  { name: 'Œil-de-Rhum',    profile: 'cautious',   tag: '🦉 Méfiant'     },
];

interface MiseOption {
  gold: number;
  goldMultiplier: number;
  itemChance: number;     // chance de gagner un item
  itemPool: string[];     // items possibles (definitionIds)
}

const MISE_OPTIONS: MiseOption[] = [
  { gold: 5,  goldMultiplier: 2,   itemChance: 0.05, itemPool: [] },
  { gold: 10, goldMultiplier: 2.5, itemChance: 0.20, itemPool: ['loaded_dice'] },
  { gold: 25, goldMultiplier: 3,   itemChance: 0.40, itemPool: ['loaded_dice', 'tavern_rum', 'player_ring'] },
];

export class TavernScene extends Phaser.Scene {
  private camp!: CampManager;
  private meta!: MetaManager;
  private sfx = new SfxManager();
  private state: GameState | null = null;
  private mise: MiseOption | null = null;
  private uiElements: Phaser.GameObjects.GameObject[] = [];
  // Sprint 80 v2 — profils d'IA des PNJ (mapping playerId → profile)
  private aiProfiles: Map<string, AiProfile> = new Map();
  // Bug #rnfl1 — Timer du tour IA, stocké pour pouvoir l'annuler.
  // Sans ça, un delayedCall(800) en vol survit aux retours lobby / nouvelles
  // parties / shutdowns de scène et joue sur un state obsolète, ce qui
  // produit les symptômes "tour qui se joue automatiquement", "ordre des
  // joueurs cassé" et "reset des dés intempestif".
  private aiTimer: Phaser.Time.TimerEvent | null = null;

  constructor() {
    super({ key: 'TavernScene' });
  }

  init(data: { camp?: CampManager; meta?: MetaManager }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
    this.state = null;
    this.mise = null;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawLobby();
  }

  // === LOBBY (sélection de mise) ===

  private drawLobby(): void {
    // Bug #rnfl1 — un timer IA en vol depuis la partie précédente jouerait
    // sur un state qui n'existe plus / a été remplacé. On purge avant de
    // redessiner le lobby.
    this.cancelAiTimer();
    this.state = null;
    this.clearUI();
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Header
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);
    this.add.text(GAME_CX, 18, '🍺 TAVERNE', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const tavernLvl = this.camp.getUpgrades().getBuildingLevel('tavern');
    this.add.text(GAME_CX, 45, `Niveau ${tavernLvl} — Or disponible : ${this.meta.getStats().totalGoldEarned - this.meta.getStats().totalGoldSpent}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Bouton retour
    const backBtn = this.add.text(15, 10, '← Camp', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, IslandCampFlag.campSceneKey(), { camp: this.camp, meta: this.meta }));

    if (tavernLvl === 0) {
      this.add.text(GAME_CX, 220, '🍺 La Taverne n\'est pas construite.\n\nAméliorez-la dans l\'Atelier\npour jouer aux dés des menteurs.', {
        fontSize: '13px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
      }).setOrigin(0.5);
      return;
    }

    // Titre + bouton règles détaillées
    this.add.text(GAME_CX, 105, 'Le Jeu des Menteurs (Liar\'s Dice)', {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);
    const tldr = 'Annonce "X dés montrent Y". Le suivant surenchérit, dit "menteur"\nou tente "proposition juste" (gagne 1 dé si exact).';
    this.add.text(GAME_CX, 130, tldr, {
      fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY, align: 'center',
    }).setOrigin(0.5, 0);

    const rulesBtn = this.add.text(GAME_CX, 152, '📜 Voir toutes les règles', {
      fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a2540', padding: { x: 10, y: 4 },
    }).setOrigin(0.5);
    rulesBtn.setInteractive({ useHandCursor: true });
    rulesBtn.on('pointerdown', () => this.showRulesModal());
    rulesBtn.on('pointerover', () => rulesBtn.setColor('#5dade2'));
    rulesBtn.on('pointerout', () => rulesBtn.setColor('#3498db'));

    // Choix de mise
    this.add.text(GAME_CX, 195, '--- Choisis ta mise ---', {
      fontSize: '12px', color: '#f39c12', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    const miseStartY = 225;
    const availableMises = MISE_OPTIONS.slice(0, tavernLvl); // niv 1 = 1 mise, niv 2 = 2, niv 3 = 3
    const playerGold = this.getPlayerGold();
    availableMises.forEach((m, i) => {
      const y = miseStartY + i * 70;
      const canAfford = playerGold >= m.gold;
      const cardW = Math.min(420, GAME_W - 30);
      const lx = GAME_CX - cardW / 2 + 10;
      const bg = this.add.rectangle(GAME_CX, y + 25, cardW, 56, canAfford ? 0x1a2540 : 0x2a1a1a)
        .setStrokeStyle(1, canAfford ? 0xf1c40f : 0x7f8c8d);
      this.add.text(lx, y + 6, `💰 Mise ${m.gold} or  →  Gain : ${Math.floor(m.gold * m.goldMultiplier)} or`, {
        fontSize: '12px', color: canAfford ? '#f1c40f' : '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'bold',
      });
      this.add.text(lx, y + 26, `Chance d'item : ${Math.round(m.itemChance * 100)}%${m.itemPool.length > 0 ? `  (${m.itemPool.length} possibles)` : ''}`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: cardW - 20 },
      });
      if (canAfford) {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => this.startGame(m));
        bg.on('pointerover', () => bg.setStrokeStyle(2, 0x2ecc71));
        bg.on('pointerout', () => bg.setStrokeStyle(1, 0xf1c40f));
      }
    });
  }

  // === PARTIE EN COURS ===

  private startGame(mise: MiseOption): void {
    // Déduire la mise
    this.spendGold(mise.gold);
    this.mise = mise;

    const tavernLvl = this.camp.getUpgrades().getBuildingLevel('tavern');
    const npcCount = tavernLvl; // niv 1 = 1 PNJ, etc.
    const players: Array<{ id: string; name: string; isHuman: boolean }> = [
      { id: 'player', name: 'Vous', isHuman: true },
    ];
    this.aiProfiles.clear();
    for (let i = 0; i < npcCount; i++) {
      const npc = NPCS[i % NPCS.length];
      const id = `npc${i}`;
      players.push({ id, name: npc.name, isHuman: false });
      this.aiProfiles.set(id, npc.profile);
    }

    this.state = initGame(players, 5);
    this.sfx.select();
    this.drawGame();

    // Si c'est au PNJ de jouer, le faire après un délai
    this.maybePlayAi();
  }

  private drawGame(): void {
    if (!this.state) return;
    this.clearUI();
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Header
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);
    this.add.text(GAME_CX, 18, `🍺 LIAR'S DICE — Manche ${this.state.round}`, {
      fontSize: '16px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const totalDice = this.state.players.reduce((sum, p) => sum + p.dice.length, 0);
    this.add.text(GAME_CX, 42, `Dés en jeu : ${totalDice}  |  Mise : ${this.mise?.gold} or`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    let y = 90;

    // Joueurs
    for (const p of this.state.players) {
      const isCurrent = this.state.players[this.state.currentPlayerIdx]?.id === p.id;
      const isAlive = p.dice.length > 0;
      const bgColor = isCurrent ? 0x1a3a2a : (isAlive ? 0x1e2d4a : 0x2a1a1a);
      const borderColor = isCurrent ? 0x2ecc71 : (isAlive ? 0x2a3a5c : 0xe74c3c);
      const bg = this.add.rectangle(GAME_CX, y + 22, Math.min(440, GAME_W - 30), 50, bgColor).setStrokeStyle(2, borderColor);
      this.uiElements.push(bg);

      // Nom + dés count + tag profil PNJ
      const profile = this.aiProfiles.get(p.id);
      const npcInfo = profile ? NPCS.find(n => n.profile === profile) : null;
      const tagSuffix = !p.isHuman && npcInfo ? `  ${npcInfo.tag}` : '';
      // Position responsive : sur mobile, le bord gauche du bg est plus proche
      // de GAME_CX (cardW=420 mobile vs 440 desktop). On utilise le bord du bg.
      const cardW = Math.min(440, GAME_W - 30);
      const nameLx = GAME_CX - cardW / 2 + 10;
      const nameTxt = this.add.text(nameLx, y + 4, `${isCurrent ? '▶ ' : '  '}${p.name} (${p.dice.length} dés)${tagSuffix}`, {
        fontSize: '12px', color: isAlive ? '#ecf0f1' : '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'bold',
      });
      this.uiElements.push(nameTxt);

      // Dés visibles uniquement pour le joueur humain ou phase 'revealed'
      const showDice = p.isHuman || this.state.phase === 'revealed' || this.state.phase === 'finished';
      const diceLabel = showDice
        ? p.dice.map(d => `[${d}]`).join(' ')
        : p.dice.map(() => '[?]').join(' ');
      const diceColor = showDice ? '#f1c40f' : '#7f8c8d';
      this.uiElements.push(this.add.text(nameLx, y + 24, diceLabel, {
        fontSize: '14px', color: diceColor, fontFamily: FONT_BODY,
      }));

      y += 60;
    }

    y += 10;

    // Enchère actuelle
    const bid = this.state.currentBid;
    if (bid) {
      const annoncer = this.state.players.find(pl => pl.id === bid.playerId);
      this.uiElements.push(this.add.text(GAME_CX, y, `📢 ${annoncer?.name} a annoncé : "${bid.count} dés montrent ${bid.face}"`, {
        fontSize: '12px', color: '#e67e22', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5));
      y += 30;
    } else {
      this.uiElements.push(this.add.text(GAME_CX, y, '🎲 Début de manche — au premier joueur de parler', {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 25;
    }

    // Phase révélée → afficher le résultat avec breakdown explicatif
    if (this.state.phase === 'revealed' && this.state.lastReveal) {
      const r = this.state.lastReveal;
      const annoncer = this.state.players.find(pl => pl.id === r.bid.playerId);
      const caller = this.state.players.find(pl => pl.id === r.callerId);
      const loser = this.state.players.find(pl => pl.id === r.loserId);

      // Verdict principal — ultra-explicite : qui a fait quoi, qui gagne, qui perd
      // Couleur user-centric : rouge si le joueur perd, vert sinon (succès = caller bien orienté)
      const playerLost = r.loserId === 'player';
      const playerWonExact = r.wasExactCall && r.callerId === 'player' && r.bidHeld;
      const verdictColor = playerLost ? '#e74c3c' : (playerWonExact ? '#f1c40f' : '#2ecc71');
      const verbose = (n: string | undefined, action: string) => n === 'Vous' ? `Vous (${action})` : `${n} (${action})`;
      let verdict: string;
      if (r.wasExactCall) {
        // Variante "Proposition juste" — Spot On
        const callerVerb = verbose(caller?.name, 'qui a tenté la proposition juste');
        if (r.bidHeld) {
          if (r.callerWonDie) {
            verdict = `🎯 EXACT ! ${annoncer?.name} avait précisément raison.\n${callerVerb} gagne 1 dé !`;
          } else {
            verdict = `🎯 EXACT ! ${annoncer?.name} avait précisément raison.\n${callerVerb} a déjà tous ses dés (pas de bonus).`;
          }
        } else {
          verdict = `❌ Pas exact ! ${callerVerb} perd 1 dé.`;
        }
      } else {
        // Appel "menteur" classique
        const callerName = verbose(caller?.name, 'qui a crié menteur');
        if (r.bidHeld) {
          // L'annonceur disait vrai → le caller s'est trompé → caller perd
          verdict = `✅ ${annoncer?.name} disait vrai !\n${callerName} perd 1 dé.`;
        } else {
          // L'annonceur bluffait → le caller a eu raison → annoncer perd
          verdict = `❌ ${annoncer?.name} bluffait !\n${callerName} avait raison — ${annoncer?.name} perd 1 dé.`;
        }
      }
      this.uiElements.push(this.add.text(GAME_CX, y, verdict, {
        fontSize: '12px', color: verdictColor, fontFamily: FONT_BODY, fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: GAME_W - 20 },
      }).setOrigin(0.5, 0));
      y += 38;

      // Cadre d'explication détaillée du comptage
      const breakdown = breakdownMatching(this.state.players, r.bid.face);
      const linesY = y;
      const explainBg = this.add.rectangle(GAME_CX, linesY + 8 + (breakdown.length + 2) * 8, Math.min(440, GAME_W - 30), 20 + (breakdown.length + 2) * 16, 0x0d1117)
        .setStrokeStyle(1, 0x2a3a5c);
      this.uiElements.push(explainBg);

      this.uiElements.push(this.add.text(GAME_CX, y + 4, `🎯 Comptage des dés montrant ${r.bid.face} (jokers = 1)`, {
        fontSize: '10px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5));
      y += 18;

      let totalReals = 0, totalJokers = 0;
      for (const b of breakdown) {
        const subtotal = b.reals + b.jokers;
        totalReals += b.reals;
        totalJokers += b.jokers;
        const lineColor = subtotal > 0 ? '#bdc3c7' : '#5a6a7a';
        const jokerNote = b.jokers > 0 ? ` + ${b.jokers} joker${b.jokers > 1 ? 's' : ''}` : '';
        this.uiElements.push(this.add.text(GAME_CX, y, `  ${b.playerName} : ${b.reals} vrai${b.reals > 1 ? 's' : ''}${jokerNote} = ${subtotal}`, {
          fontSize: '10px', color: lineColor, fontFamily: FONT_BODY,
        }).setOrigin(0.5));
        y += 14;
      }
      // Ligne total + comparaison à l'enchère
      // Couleur factuelle : vert si l'enchère est tenue (≥), rouge sinon (<)
      const symbol = r.bidHeld ? '≥' : '<';
      const factualColor = r.bidHeld ? '#2ecc71' : '#e74c3c';
      this.uiElements.push(this.add.text(GAME_CX, y + 2, `Total : ${totalReals}+${totalJokers} = ${r.actualCount}  ${symbol}  ${r.bid.count} (annoncé)`, {
        fontSize: '11px', color: factualColor, fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5));
      y += 22;

      // Conséquence : qui perd / gagne quoi
      let consequenceText: string;
      if (r.wasExactCall && r.bidHeld && r.callerWonDie) {
        consequenceText = `${caller?.name} → ${caller?.dice.length} dés (+1)`;
      } else {
        consequenceText = `${loser?.name} → ${loser?.dice.length} dés restants`;
      }
      this.uiElements.push(this.add.text(GAME_CX, y, consequenceText, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 24;

      // Bouton continuer après révélation
      const continueBtn = this.add.text(GAME_CX, y, '[ Continuer ]', {
        fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a2a1a', padding: { x: 14, y: 6 },
      }).setOrigin(0.5);
      this.uiElements.push(continueBtn);
      continueBtn.setInteractive({ useHandCursor: true });
      continueBtn.on('pointerdown', () => {
        if (!this.state) return;
        continueAfterReveal(this.state);
        this.drawGame();
        this.maybePlayAi();
      });
      return;
    }

    // Phase finie → annoncer gagnant + récompenses
    if (this.state.phase === 'finished') {
      this.drawEndGame(y);
      return;
    }

    // Au tour du joueur humain → boutons d'action
    const current = this.state.players[this.state.currentPlayerIdx];
    if (current?.isHuman) {
      this.drawHumanActions(y);
    } else {
      this.uiElements.push(this.add.text(GAME_CX, y, `⏳ ${current?.name} réfléchit...`, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'italic',
      }).setOrigin(0.5));
    }
  }

  private drawHumanActions(y: number): void {
    if (!this.state) return;
    const current = this.state.players[this.state.currentPlayerIdx];

    // 3 boutons côte à côte quand une enchère existe : Menteur / Proposition juste / Surenchérir
    // Sinon (ouverture de manche), juste Surenchérir centré.
    if (this.state.currentBid) {
      // Sur mobile, on stacke les 3 boutons en 2 lignes (Menteur+Surenchérir au-dessus,
      // Proposition juste en dessous) pour éviter le chevauchement.
      const _mobTavern = GAME_W < 500;
      const liarX = _mobTavern ? GAME_CX - 90 : GAME_CX - 130;
      const exactX = GAME_CX;
      const raiseX = _mobTavern ? GAME_CX + 90 : GAME_CX + 130;
      const exactY = _mobTavern ? y + 50 : y + 20;

      // Bouton Menteur (gauche)
      const liarBtn = this.add.text(liarX, y + 20, '[ MENTEUR ! ]', {
        fontSize: _mobTavern ? '11px' : '12px', color: '#e74c3c', fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#2c1a1a', padding: { x: _mobTavern ? 6 : 10, y: 6 },
      }).setOrigin(0.5);
      this.uiElements.push(liarBtn);
      liarBtn.setInteractive({ useHandCursor: true });
      liarBtn.on('pointerdown', () => {
        if (!this.state) return;
        this.sfx.select();
        callLiar(this.state);
        this.drawGame();
      });

      // Bouton Proposition juste (centre) — variante Spot On / Calza
      // Risque/reward : exact ⇒ +1 dé, sinon ⇒ -1 dé
      const exactBtn = this.add.text(exactX, exactY, '[ PROPOSITION JUSTE ]', {
        fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#2c2a1a', padding: { x: 8, y: 6 },
      }).setOrigin(0.5);
      this.uiElements.push(exactBtn);
      exactBtn.setInteractive({ useHandCursor: true });
      exactBtn.on('pointerdown', () => {
        if (!this.state) return;
        this.sfx.select();
        callExact(this.state);
        this.drawGame();
      });

      // Bouton Surenchérir (droite)
      const raiseBtn = this.add.text(raiseX, y + 20, '[ Surenchérir ]', {
        fontSize: _mobTavern ? '11px' : '12px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#1a2a1a', padding: { x: _mobTavern ? 6 : 10, y: 6 },
      }).setOrigin(0.5);
      this.uiElements.push(raiseBtn);
      raiseBtn.setInteractive({ useHandCursor: true });
      raiseBtn.on('pointerdown', () => this.showBidPicker(current.dice.length));
    } else {
      const raiseBtn = this.add.text(GAME_CX, y + 20, '[ Annoncer une enchère ]', {
        fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#1a2a1a', padding: { x: 14, y: 6 },
      }).setOrigin(0.5);
      this.uiElements.push(raiseBtn);
      raiseBtn.setInteractive({ useHandCursor: true });
      raiseBtn.on('pointerdown', () => this.showBidPicker(current.dice.length));
    }
  }

  /** Modale simple pour choisir une enchère (count + face). */
  private showBidPicker(maxCount: number): void {
    if (!this.state) return;
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.8).setDepth(200).setInteractive();
    const elements: Phaser.GameObjects.GameObject[] = [overlay];

    const totalDice = this.state.players.reduce((sum, p) => sum + p.dice.length, 0);
    const minCount = this.state.currentBid ? this.state.currentBid.count : 1;
    const minFace = this.state.currentBid ? this.state.currentBid.face : 1;

    let selectedCount = minCount;
    let selectedFace: DiceFace = minFace;

    const title = this.add.text(GAME_CX, 100, '📢 Annoncer une enchère', {
      fontSize: '15px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    elements.push(title);

    // Indicateurs
    const countLabel = this.add.text(GAME_CX, 150, `Nombre de dés : ${selectedCount}`, {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(201);
    elements.push(countLabel);

    const faceLabel = this.add.text(GAME_CX, 220, `Face : ${selectedFace}`, {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(201);
    elements.push(faceLabel);

    const validateLabel = this.add.text(GAME_CX, 290, '', {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(201);
    elements.push(validateLabel);

    const updateLabels = () => {
      countLabel.setText(`Nombre de dés : ${selectedCount}`);
      faceLabel.setText(`Face : ${selectedFace}`);
      const valid = this.state!.currentBid
        ? (selectedCount > this.state!.currentBid.count) || (selectedCount === this.state!.currentBid.count && selectedFace > this.state!.currentBid.face)
        : true;
      validateLabel.setText(valid ? '✓ Enchère valide' : '✗ Doit être supérieure à l\'enchère précédente');
      validateLabel.setColor(valid ? '#2ecc71' : '#e74c3c');
    };
    updateLabels();

    // Boutons count -/+
    const countDownBtn = this.add.text(GAME_CX - 90, 150, '[-]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY, backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    countDownBtn.setInteractive({ useHandCursor: true });
    countDownBtn.on('pointerdown', () => { if (selectedCount > 1) selectedCount--; updateLabels(); });
    elements.push(countDownBtn);

    const countUpBtn = this.add.text(GAME_CX + 90, 150, '[+]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY, backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    countUpBtn.setInteractive({ useHandCursor: true });
    countUpBtn.on('pointerdown', () => { if (selectedCount < totalDice) selectedCount++; updateLabels(); });
    elements.push(countUpBtn);

    // Boutons face -/+
    const faceDownBtn = this.add.text(GAME_CX - 90, 220, '[-]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY, backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    faceDownBtn.setInteractive({ useHandCursor: true });
    faceDownBtn.on('pointerdown', () => { if (selectedFace > 1) selectedFace = (selectedFace - 1) as DiceFace; updateLabels(); });
    elements.push(faceDownBtn);

    const faceUpBtn = this.add.text(GAME_CX + 90, 220, '[+]', {
      fontSize: '14px', color: '#bdc3c7', fontFamily: FONT_BODY, backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    faceUpBtn.setInteractive({ useHandCursor: true });
    faceUpBtn.on('pointerdown', () => { if (selectedFace < 6) selectedFace = (selectedFace + 1) as DiceFace; updateLabels(); });
    elements.push(faceUpBtn);

    // Confirmer / Annuler
    const confirmBtn = this.add.text(GAME_CX - 70, 360, '[ Confirmer ]', {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(201);
    confirmBtn.setInteractive({ useHandCursor: true });
    confirmBtn.on('pointerdown', () => {
      if (!this.state) return;
      const bid: Omit<Bid, 'playerId'> = { count: selectedCount, face: selectedFace };
      const before = this.state.currentBid;
      placeBid(this.state, bid);
      // Si pas changé, l'enchère a été refusée → on garde la modale
      if (this.state.currentBid === before) return;
      this.sfx.select();
      elements.forEach(e => e.destroy());
      this.drawGame();
      this.maybePlayAi();
    });
    elements.push(confirmBtn);

    const cancelBtn = this.add.text(GAME_CX + 70, 360, '[ Annuler ]', {
      fontSize: '13px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => elements.forEach(e => e.destroy()));
    elements.push(cancelBtn);
  }

  /** Si c'est au tour d'un PNJ, fait jouer l'IA après un petit délai. */
  private maybePlayAi(): void {
    // Bug #rnfl1 — toujours annuler le timer précédent avant d'en programmer
    // un nouveau (évite les empilements en cas d'appel récursif rapide ou de
    // changement d'état pendant les 800 ms d'attente).
    this.cancelAiTimer();
    if (!this.state || this.state.phase !== 'bidding') return;
    const current = this.state.players[this.state.currentPlayerIdx];
    if (current?.isHuman) return;
    this.aiTimer = this.time.delayedCall(800, () => {
      this.aiTimer = null;
      // Double check : l'état peut avoir changé pendant les 800 ms (retour
      // lobby, fin de partie, nouvelle partie démarrée). On bail si oui.
      if (!this.state || this.state.phase !== 'bidding') return;
      const stillCurrent = this.state.players[this.state.currentPlayerIdx];
      if (!stillCurrent || stillCurrent.id !== current.id || stillCurrent.isHuman) return;
      // Profil de ce PNJ (défaut balanced si non défini)
      const profile = this.aiProfiles.get(current.id) ?? 'balanced';
      const decision = aiDecide(this.state, profile);
      if (decision.action === 'call') {
        callLiar(this.state);
      } else if (decision.action === 'exact') {
        callExact(this.state);
      } else {
        placeBid(this.state, decision.bid);
      }
      this.drawGame();
      this.maybePlayAi();
    });
  }

  /** Annule le timer IA en vol, si présent. Idempotent. */
  private cancelAiTimer(): void {
    if (this.aiTimer) {
      this.aiTimer.remove(false);
      this.aiTimer = null;
    }
  }

  // === FIN DE PARTIE ===

  private drawEndGame(y: number): void {
    if (!this.state || !this.mise) return;
    const winnerId = getWinnerId(this.state);
    const playerWon = winnerId === 'player';

    // Bandeau résultat
    this.uiElements.push(this.add.text(GAME_CX, y + 20, playerWon ? '🏆 VICTOIRE !' : '💀 Défaite...', {
      fontSize: '24px', color: playerWon ? '#2ecc71' : '#e74c3c', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5));

    // Récompenses
    if (playerWon) {
      const goldWon = Math.floor(this.mise.gold * this.mise.goldMultiplier);
      this.addPlayerGold(goldWon);
      this.uiElements.push(this.add.text(GAME_CX, y + 60, `💰 +${goldWon} or`, {
        fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY,
      }).setOrigin(0.5));

      // Chance d'item
      let itemMsg = '';
      if (this.mise.itemPool.length > 0 && Math.random() < this.mise.itemChance) {
        const itemId = this.mise.itemPool[Math.floor(Math.random() * this.mise.itemPool.length)];
        const stored = this.camp.addItemToStorage(itemId);
        if (stored) itemMsg = `🎁 ${itemId} ajouté au coffre !`;
      }
      if (itemMsg) {
        this.uiElements.push(this.add.text(GAME_CX, y + 85, itemMsg, {
          fontSize: '12px', color: '#f1c40f', fontFamily: FONT_BODY,
        }).setOrigin(0.5));
      }
    } else {
      this.uiElements.push(this.add.text(GAME_CX, y + 60, `💸 Tu perds ta mise (${this.mise.gold} or déjà retiré)`, {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
    }

    // Bouton retour lobby
    const replayBtn = this.add.text(GAME_CX, y + 130, '[ Rejouer ]', {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 14, y: 6 },
    }).setOrigin(0.5);
    this.uiElements.push(replayBtn);
    replayBtn.setInteractive({ useHandCursor: true });
    replayBtn.on('pointerdown', () => this.drawLobby());
  }

  // === HELPERS ===

  private clearUI(): void {
    this.children.removeAll(true);
    this.uiElements = [];
  }

  /**
   * Or disponible : actuellement on ne stocke pas d'or persistant entre runs au camp.
   * On utilise les stats meta (totalGoldEarned - totalGoldSpent) comme proxy.
   * Une vraie banque or serait à implémenter dans un sprint futur.
   */
  private getPlayerGold(): number {
    const stats = this.meta.getStats();
    return Math.max(0, stats.totalGoldEarned - stats.totalGoldSpent);
  }

  private spendGold(amount: number): void {
    this.meta.addStat('totalGoldSpent', amount);
  }

  private addPlayerGold(amount: number): void {
    this.meta.addStat('totalGoldEarned', amount);
  }

  /**
   * Bug #rnfl1 — annule le timer IA quand on quitte la scène, sinon un
   * delayedCall en vol s'exécute après la fermeture et plante en accédant
   * à un state nullifié (ou pire, mute un state d'une autre scène).
   */
  shutdown(): void {
    this.cancelAiTimer();
  }

  /** Affiche les règles complètes du Liar's Dice dans une modale dédiée. */
  private showRulesModal(): void {
    const elements: Phaser.GameObjects.GameObject[] = [];
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.92).setDepth(200).setInteractive();
    elements.push(overlay);

    const title = this.add.text(GAME_CX, 50, '📜 Règles du Jeu des Menteurs', {
      fontSize: '17px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    elements.push(title);

    // Règles structurées en sections
    const rulesText = [
      '🎲 INSTALLATION',
      'Chacun lance 5 dés cachés. Tu ne vois que les tiens.',
      '',
      '📢 ANNONCE',
      'Chaque tour, tu déclares "X dés montrent face Y"',
      '(ex: "trois 4" = au moins 3 dés de la face 4 sont en jeu).',
      '',
      '⬆️ SURENCHÈRE',
      'Le joueur suivant doit soit :',
      '  • Surenchérir : monter le COUNT (X+ dés) OU la FACE (Y+),',
      '    jamais reculer sur les deux',
      '  • Crier "MENTEUR !" pour contester l\'enchère',
      '',
      '🃏 LES 1 SONT JOKERS',
      'Les dés montrant 1 comptent comme N\'IMPORTE QUELLE face,',
      'sauf si la face annoncée est 1 (alors comptage strict).',
      'Exemple : annonce "trois 4" — un 4 + deux 1 = 3 dés OK.',
      '',
      '⚖️ RÉSOLUTION DU "MENTEUR"',
      'On révèle tous les dés et on compte. Si l\'enchère est :',
      '  • TENUE (≥ X dés) → le contestataire perd 1 dé',
      '  • FAUSSE (< X dés) → l\'annonceur perd 1 dé',
      '',
      '🎯 "PROPOSITION JUSTE" (Spot On)',
      'Au lieu de "menteur", tu peux dire "proposition juste"',
      'pour parier que l\'enchère est PILE EXACTE.',
      '  • EXACT (= X dés pile) → tu GAGNES 1 dé (cap 5)',
      '  • PAS EXACT (< ou > X) → tu perds 1 dé',
      '',
      '☠️ ÉLIMINATION',
      'À 0 dé, tu es éliminé. Le dernier debout gagne la partie',
      'et empoche les récompenses (or × multiplicateur + chance d\'item).',
    ].join('\n');

    const body = this.add.text(GAME_CX, 80, rulesText, {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
      align: 'left', lineSpacing: 2, wordWrap: { width: GAME_W - 30 },
    }).setOrigin(0.5, 0).setDepth(201);
    elements.push(body);

    // Bouton fermer
    const closeBtn = this.add.text(GAME_CX, GAME_H - 50, '[ J\'ai compris ]', {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setDepth(201);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => elements.forEach(e => e.destroy()));
    closeBtn.on('pointerover', () => closeBtn.setColor('#27ae60'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#2ecc71'));
    elements.push(closeBtn);
  }
}
